import { useState, useRef, useEffect } from "react";
import "@/App.css";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Image as ImageIcon, Settings, Info, Zap, GitCompare, PackageOpen, X, ChevronDown } from "lucide-react";
import imageCompression from "browser-image-compression";
import JSZip from "jszip";
import { saveAs } from "file-saver";

function App() {
  const [images, setImages] = useState([]);
  const [quality, setQuality] = useState([80]);
  const [format, setFormat] = useState("jpeg");
  const [maxWidth, setMaxWidth] = useState([0]);
  const [maxHeight, setMaxHeight] = useState([0]);
  const [compressionPreset, setCompressionPreset] = useState("balanced");
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingImageId, setProcessingImageId] = useState(null);
  const [comparisonMode, setComparisonMode] = useState(false);
  const fileInputRef = useRef(null);
  const updateTimerRef = useRef({});
  const globalUpdateTimerRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith("image/"));
    if (files.length > 0) {
      processMultipleImages(files);
    } else {
      toast.error("Please drop valid image files");
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      processMultipleImages(files);
    }
  };

  const processMultipleImages = async (files) => {
    setIsProcessing(true);
    const imagePromises = files.map((file) => processImage(file));
    const processedImages = await Promise.all(imagePromises);
    setImages(processedImages);
    setIsProcessing(false);
    toast.success(`${files.length} image${files.length > 1 ? 's' : ''} uploaded!`);
  };

  const processImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = async () => {
          const imageData = {
            id: Math.random().toString(36).substr(2, 9),
            name: file.name,
            originalSrc: e.target.result,
            originalSize: file.size,
            originalWidth: img.width,
            originalHeight: img.height,
            file: file,
            compressed: null,
            comparisonData: null,
            // Individual settings for this image
            settings: {
              quality: quality[0],
              format: format,
              maxWidth: maxWidth[0],
              maxHeight: maxHeight[0],
              preset: compressionPreset
            }
          };
          
          if (comparisonMode) {
            // Process with all presets for comparison
            const comparisonData = await compressWithAllPresets(file, imageData.settings);
            imageData.comparisonData = comparisonData;
          } else {
            // Process with selected preset
            const compressed = await compressImage(file, imageData.settings);
            imageData.compressed = compressed;
          }
          
          resolve(imageData);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const compressWithAllPresets = async (file, settings) => {
    const presets = ["quality", "balanced", "maximum"];
    const results = {};
    
    for (const preset of presets) {
      const presetSettings = { ...settings, preset };
      results[preset] = await compressImage(file, presetSettings);
    }
    
    return results;
  };

  const compressImage = async (file, settings) => {
    try {
      let targetWidth = settings.maxWidth > 0 ? settings.maxWidth : undefined;
      let targetHeight = settings.maxHeight > 0 ? settings.maxHeight : undefined;
      
      let maxSizeMB = 10;
      let compressionQuality = settings.quality / 100;
      
      if (settings.preset === "maximum") {
        maxSizeMB = 0.1;
        compressionQuality = Math.min(settings.quality / 100, 0.7);
      } else if (settings.preset === "balanced") {
        maxSizeMB = 1;
        compressionQuality = settings.quality / 100;
      } else if (settings.preset === "quality") {
        maxSizeMB = 5;
        compressionQuality = Math.max(settings.quality / 100, 0.8);
      }
      
      const options = {
        maxSizeMB: maxSizeMB,
        maxWidthOrHeight: targetWidth || targetHeight || undefined,
        useWebWorker: true,
        quality: compressionQuality,
        fileType: settings.format === "png" ? "image/png" : settings.format === "webp" ? "image/webp" : "image/jpeg",
        initialQuality: compressionQuality,
        alwaysKeepResolution: false,
        maxIteration: 20,
      };
      
      const compressedFile = await imageCompression(file, options);
      const url = URL.createObjectURL(compressedFile);
      
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          resolve({
            src: url,
            blob: compressedFile,
            size: compressedFile.size,
            width: img.width,
            height: img.height,
          });
        };
        img.src = url;
      });
    } catch (error) {
      console.error("Compression error:", error);
      return null;
    }
  };

  const recompressAll = async () => {
    if (images.length === 0) return;
    
    setIsProcessing(true);
    const updatedImages = await Promise.all(
      images.map(async (img) => {
        if (comparisonMode) {
          const comparisonData = await compressWithAllPresets(img.file, img.settings);
          return { ...img, comparisonData };
        } else {
          const compressed = await compressImage(img.file, img.settings);
          return { ...img, compressed };
        }
      })
    );
    setImages(updatedImages);
    setIsProcessing(false);
  };

  const updateImageSettings = async (imageId, newSettings) => {
    // Find the image
    const imageIndex = images.findIndex(img => img.id === imageId);
    if (imageIndex === -1) return;
    
    // Clear existing timer for this image
    if (updateTimerRef.current[imageId]) {
      clearTimeout(updateTimerRef.current[imageId]);
    }
    
    // Update settings immediately in UI
    setImages(prevImages => {
      const updated = [...prevImages];
      updated[imageIndex] = {
        ...updated[imageIndex],
        settings: { ...updated[imageIndex].settings, ...newSettings }
      };
      return updated;
    });
    
    // Debounce the actual recompression
    updateTimerRef.current[imageId] = setTimeout(async () => {
      setProcessingImageId(imageId);
      
      try {
        const currentImage = images[imageIndex];
        const updatedSettings = { ...currentImage.settings, ...newSettings };
        
        let compressed;
        let comparisonData;
        
        if (comparisonMode) {
          comparisonData = await compressWithAllPresets(currentImage.file, updatedSettings);
        } else {
          compressed = await compressImage(currentImage.file, updatedSettings);
        }
        
        // Update state with compressed result
        setImages(prevImages => {
          const finalImages = [...prevImages];
          const finalIndex = finalImages.findIndex(img => img.id === imageId);
          
          if (finalIndex !== -1) {
            finalImages[finalIndex] = {
              ...finalImages[finalIndex],
              settings: updatedSettings,
              compressed: compressed || finalImages[finalIndex].compressed,
              comparisonData: comparisonData || finalImages[finalIndex].comparisonData
            };
          }
          
          return finalImages;
        });
      } catch (error) {
        console.error("Error recompressing image:", error);
      } finally {
        setProcessingImageId(null);
      }
    }, 800);
  };

  const handleDownloadSingle = (image) => {
    if (!image.compressed) return;
    
    const link = document.createElement("a");
    link.href = image.compressed.src;
    const extension = format === "png" ? "png" : format === "webp" ? "webp" : "jpg";
    const nameWithoutExt = image.name.replace(/\.[^/.]+$/, "");
    link.download = `${nameWithoutExt}_compressed.${extension}`;
    link.click();
    toast.success("Download started!");
  };

  const handleDownloadAll = async () => {
    if (images.length === 0) return;
    
    const zip = new JSZip();
    const extension = format === "png" ? "png" : format === "webp" ? "webp" : "jpg";
    
    for (const image of images) {
      if (image.compressed) {
        const nameWithoutExt = image.name.replace(/\.[^/.]+$/, "");
        zip.file(`${nameWithoutExt}_compressed.${extension}`, image.compressed.blob);
      }
    }
    
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, `compressed_images_${Date.now()}.zip`);
    toast.success("All images downloaded!");
  };

  const handleReset = () => {
    setImages([]);
    setComparisonMode(false);
    toast.info("Ready for new images");
  };

  const handleRemoveImage = (imageId) => {
    const updatedImages = images.filter(img => img.id !== imageId);
    setImages(updatedImages);
    
    if (updatedImages.length === 0) {
      setComparisonMode(false);
    }
    
    toast.success("Image removed");
  };

  // Auto-update all images when global settings change
  useEffect(() => {
    if (images.length > 0 && !isProcessing) {
      // Clear existing timer
      if (globalUpdateTimerRef.current) {
        clearTimeout(globalUpdateTimerRef.current);
      }
      
      // Debounce the update
      globalUpdateTimerRef.current = setTimeout(async () => {
        setIsProcessing(true);
        
        try {
          const updatedImages = await Promise.all(
            images.map(async (img) => {
              // Update global settings to each image
              const updatedSettings = {
                quality: quality[0],
                format: format,
                maxWidth: maxWidth[0],
                maxHeight: maxHeight[0],
                preset: compressionPreset
              };
              
              let compressed;
              let comparisonData;
              
              if (comparisonMode) {
                comparisonData = await compressWithAllPresets(img.file, updatedSettings);
              } else {
                compressed = await compressImage(img.file, updatedSettings);
              }
              
              return {
                ...img,
                settings: updatedSettings,
                compressed: compressed || img.compressed,
                comparisonData: comparisonData || img.comparisonData
              };
            })
          );
          
          setImages(updatedImages);
        } catch (error) {
          console.error("Error recompressing images:", error);
        } finally {
          setIsProcessing(false);
        }
      }, 800);
    }
  }, [quality, format, maxWidth, maxHeight, compressionPreset, comparisonMode]);

  const formatBytes = (bytes, allowNegative = false) => {
    if (bytes === 0) return "0 Bytes";
    const isNegative = bytes < 0;
    const absBytes = Math.abs(bytes);
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(absBytes) / Math.log(k));
    const formatted = Math.round((absBytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    return allowNegative && isNegative ? "+" + formatted : formatted;
  };

  const calculateTotalStats = () => {
    let totalOriginal = 0;
    let totalCompressed = 0;
    
    images.forEach(img => {
      totalOriginal += img.originalSize;
      if (img.compressed) {
        totalCompressed += img.compressed.size;
      }
    });
    
    const savings = totalOriginal - totalCompressed;
    const percentage = totalOriginal > 0 ? Math.round((savings / totalOriginal) * 100) : 0;
    
    return { totalOriginal, totalCompressed, savings, percentage };
  };

  const toggleComparisonMode = async () => {
    const newMode = !comparisonMode;
    setComparisonMode(newMode);
    
    if (images.length > 0) {
      setIsProcessing(true);
      const updatedImages = await Promise.all(
        images.map(async (img) => {
          if (newMode) {
            const comparisonData = await compressWithAllPresets(img.file);
            return { ...img, comparisonData };
          } else {
            const compressed = await compressImage(img.file, compressionPreset);
            return { ...img, compressed };
          }
        })
      );
      setImages(updatedImages);
      setIsProcessing(false);
    }
  };

  const stats = calculateTotalStats();

  return (
    <div className="min-h-screen bg-slate-50 grid-pattern">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 md:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                ImagePress
              </h1>
            </div>
            <Badge variant="secondary" className="hidden md:flex items-center gap-1 px-3 py-1">
              <Info className="w-3 h-3" />
              100% Local Processing
            </Badge>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 md:py-12">
        {/* Upload Zone */}
        {images.length === 0 && (
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-5xl font-bold mb-4 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Compress Your Images
              </h2>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed">
                Reduce file size without losing quality. All processing happens in your browser.
              </p>
            </div>

            <div
              data-testid="upload-zone"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`upload-zone h-80 border-4 border-dashed rounded-xl cursor-pointer ${
                isDragging ? "drag-over" : "border-slate-300"
              } bg-white hover:bg-slate-50 flex flex-col items-center justify-center gap-4`}
            >
              <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center">
                <Upload className="w-10 h-10 text-blue-600" />
              </div>
              <div className="text-center">
                <p className="text-xl font-semibold text-slate-900 mb-2">
                  Drop your images here
                </p>
                <p className="text-sm text-slate-500">or click to browse • Multiple files supported</p>
              </div>
              <Badge variant="outline" className="text-xs">JPEG, PNG, WebP supported</Badge>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
            </div>
          </div>
        )}

        {/* Workspace */}
        {images.length > 0 && (
          <div className="space-y-6">
            {/* Total Stats & Actions */}
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 flex-1 w-full">
                <Card className="stat-card border-slate-200 shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Total Original</p>
                    <p className="text-xl font-bold text-slate-900">{formatBytes(stats.totalOriginal)}</p>
                  </CardContent>
                </Card>

                <Card className="stat-card border-slate-200 shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">After Compressed</p>
                    <p className="text-xl font-bold text-blue-600">{formatBytes(stats.totalCompressed)}</p>
                  </CardContent>
                </Card>

                <Card className="stat-card border-emerald-200 bg-emerald-50 shadow-sm">
                  <CardContent className="p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1">Total Saved</p>
                    <p className="text-xl font-bold text-emerald-600">
                      {stats.percentage}%
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="flex flex-wrap gap-2 w-full lg:w-auto">
                {!comparisonMode && (
                  <>
                    <Button
                      data-testid="download-single-button"
                      onClick={() => images.length === 1 ? handleDownloadSingle(images[0]) : handleDownloadAll()}
                      disabled={isProcessing}
                      className="btn-primary rounded-full px-6 py-6 shadow-lg shadow-blue-500/20 font-bold bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {images.length === 1 ? 'Download' : `Download All (${images.length})`}
                    </Button>
                  </>
                )}
                <Button
                  data-testid="new-image-button"
                  onClick={handleReset}
                  variant="outline"
                  className="rounded-full px-6 py-6 border-2 border-slate-200 hover:border-slate-300 bg-transparent text-slate-700 font-medium"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  New Images
                </Button>
              </div>
            </div>

            {/* Controls */}
            <Card className="control-panel border-white/20 shadow-xl">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Compression Settings</h3>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-semibold text-slate-700">Comparison Mode</label>
                    <Switch
                      checked={comparisonMode}
                      onCheckedChange={toggleComparisonMode}
                      data-testid="comparison-toggle"
                    />
                    <GitCompare className="w-4 h-4 text-slate-500" />
                  </div>
                </div>

                {!comparisonMode && (
                  <div className="space-y-6">
                    <div>
                      <label className="text-sm font-semibold text-slate-700 mb-2 block">Compression Preset</label>
                      <Select value={compressionPreset} onValueChange={(val) => { setCompressionPreset(val); }}>
                        <SelectTrigger data-testid="preset-select" className="bg-slate-50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="quality">High Quality (Larger file)</SelectItem>
                          <SelectItem value="balanced">Balanced (Recommended)</SelectItem>
                          <SelectItem value="maximum">Maximum Compression (Smallest file)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1">
                        {compressionPreset === "maximum" && "Aggressive compression for web/email - achieves 200KB → 25KB"}
                        {compressionPreset === "balanced" && "Good balance between quality and file size"}
                        {compressionPreset === "quality" && "Minimal compression, preserves maximum quality"}
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <label className="text-sm font-semibold text-slate-700">Quality</label>
                        <Badge variant="secondary" data-testid="quality-value">{quality[0]}%</Badge>
                      </div>
                      <Slider
                        data-testid="quality-slider"
                        value={quality}
                        onValueChange={setQuality}
                        min={1}
                        max={100}
                        step={1}
                        className="w-full"
                      />
                    </div>

                    <Separator />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-semibold text-slate-700 mb-2 block">Format</label>
                        <Select value={format} onValueChange={setFormat}>
                          <SelectTrigger data-testid="format-select" className="bg-slate-50">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="jpeg">JPEG</SelectItem>
                            <SelectItem value="png">PNG</SelectItem>
                            <SelectItem value="webp">WebP</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="text-sm font-semibold text-slate-700 mb-2 block">Max Width (px)</label>
                        <Slider
                          data-testid="width-slider"
                          value={maxWidth}
                          onValueChange={setMaxWidth}
                          min={0}
                          max={4000}
                          step={100}
                          className="w-full"
                        />
                        <p className="text-xs text-slate-500 mt-1">{maxWidth[0] === 0 ? "Original" : maxWidth[0]}</p>
                      </div>

                      <div>
                        <label className="text-sm font-semibold text-slate-700 mb-2 block">Max Height (px)</label>
                        <Slider
                          data-testid="height-slider"
                          value={maxHeight}
                          onValueChange={setMaxHeight}
                          min={0}
                          max={4000}
                          step={100}
                          className="w-full"
                        />
                        <p className="text-xs text-slate-500 mt-1">{maxHeight[0] === 0 ? "Original" : maxHeight[0]}</p>
                      </div>
                    </div>
                  </div>
                )}

                {comparisonMode && (
                  <div className="text-center py-4">
                    <p className="text-slate-600">
                      <GitCompare className="w-5 h-5 inline mr-2" />
                      Viewing all presets side-by-side. Toggle off to customize settings.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Images Grid */}
            <div className="space-y-8">
              {images.map((image) => (
                <Card key={image.id} className="border-slate-200 shadow-sm relative">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-bold text-slate-900 truncate flex-1">{image.name}</h3>
                      <div className="flex items-center gap-2">
                        {!comparisonMode && (
                          <Button
                            size="sm"
                            onClick={() => handleDownloadSingle(image)}
                            disabled={!image.compressed}
                            className="rounded-full"
                            data-testid={`download-image-${image.id}`}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Download
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRemoveImage(image.id)}
                          className="rounded-full border-red-200 hover:bg-red-50 hover:border-red-300 text-red-600"
                          data-testid={`remove-image-${image.id}`}
                        >
                          <X className="w-3 h-3 mr-1" />
                          Remove
                        </Button>
                      </div>
                    </div>

                    {/* Individual Settings for Multiple Images */}
                    {images.length > 1 && !comparisonMode && (
                      <Collapsible className="mb-4">
                        <CollapsibleTrigger className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blue-600 transition-colors">
                          <Settings className="w-4 h-4" />
                          Individual Settings
                          <ChevronDown className="w-4 h-4" />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                          <div className="space-y-4">
                            <div>
                              <label className="text-xs font-semibold text-slate-700 mb-2 block">Preset</label>
                              <Select 
                                value={image.settings.preset} 
                                onValueChange={(val) => updateImageSettings(image.id, { preset: val })}
                              >
                                <SelectTrigger className="h-9 text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="quality">High Quality</SelectItem>
                                  <SelectItem value="balanced">Balanced</SelectItem>
                                  <SelectItem value="maximum">Maximum</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <div className="flex justify-between items-center mb-2">
                                <label className="text-xs font-semibold text-slate-700">Quality</label>
                                <Badge variant="secondary" className="text-xs">{image.settings.quality}%</Badge>
                              </div>
                              <Slider
                                value={[image.settings.quality]}
                                onValueChange={(val) => updateImageSettings(image.id, { quality: val[0] })}
                                min={1}
                                max={100}
                                step={1}
                                className="w-full"
                              />
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <label className="text-xs font-semibold text-slate-700 mb-2 block">Format</label>
                                <Select 
                                  value={image.settings.format} 
                                  onValueChange={(val) => updateImageSettings(image.id, { format: val })}
                                >
                                  <SelectTrigger className="h-9 text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="jpeg">JPEG</SelectItem>
                                    <SelectItem value="png">PNG</SelectItem>
                                    <SelectItem value="webp">WebP</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              <div>
                                <label className="text-xs font-semibold text-slate-700 mb-2 block">Max W</label>
                                <Slider
                                  value={[image.settings.maxWidth]}
                                  onValueChange={(val) => updateImageSettings(image.id, { maxWidth: val[0] })}
                                  min={0}
                                  max={4000}
                                  step={100}
                                  className="w-full mt-2"
                                />
                                <p className="text-xs text-slate-500 mt-1">
                                  {image.settings.maxWidth === 0 ? "Orig" : image.settings.maxWidth}
                                </p>
                              </div>

                              <div>
                                <label className="text-xs font-semibold text-slate-700 mb-2 block">Max H</label>
                                <Slider
                                  value={[image.settings.maxHeight]}
                                  onValueChange={(val) => updateImageSettings(image.id, { maxHeight: val[0] })}
                                  min={0}
                                  max={4000}
                                  step={100}
                                  className="w-full mt-2"
                                />
                                <p className="text-xs text-slate-500 mt-1">
                                  {image.settings.maxHeight === 0 ? "Orig" : image.settings.maxHeight}
                                </p>
                              </div>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}

                    {!comparisonMode && image.compressed && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Original</span>
                            <Badge variant="outline" className="text-xs">
                              {formatBytes(image.originalSize)}
                            </Badge>
                          </div>
                          <div className="image-container rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                            <img src={image.originalSrc} alt="Original" className="w-full h-auto" />
                          </div>
                        </div>

                        <div className="relative">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Compressed</span>
                            <Badge className="bg-emerald-600 text-white text-xs">
                              {formatBytes(image.compressed.size)} ({Math.round(((image.originalSize - image.compressed.size) / image.originalSize) * 100)}% saved)
                            </Badge>
                          </div>
                          <div className="image-container rounded-lg overflow-hidden bg-slate-100 border border-blue-200 relative">
                            {processingImageId === image.id && (
                              <div className="processing-overlay absolute inset-0 bg-white/60 flex items-center justify-center z-10 backdrop-blur-sm">
                                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
                              </div>
                            )}
                            <img src={image.compressed.src} alt="Compressed" className="w-full h-auto" />
                          </div>
                        </div>
                      </div>
                    )}

                    {comparisonMode && image.comparisonData && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {["quality", "balanced", "maximum"].map((preset) => {
                          const data = image.comparisonData[preset];
                          if (!data) return null;
                          
                          const savings = Math.round(((image.originalSize - data.size) / image.originalSize) * 100);
                          
                          return (
                            <div key={preset}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                  {preset === "quality" ? "High Quality" : preset === "balanced" ? "Balanced" : "Maximum"}
                                </span>
                                <Badge className={`text-xs ${
                                  preset === "maximum" ? "bg-green-600" : 
                                  preset === "balanced" ? "bg-blue-600" : "bg-slate-600"
                                } text-white`}>
                                  {savings}% off
                                </Badge>
                              </div>
                              <div className="image-container rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                                <img src={data.src} alt={preset} className="w-full h-auto" />
                              </div>
                              <p className="text-xs text-slate-500 mt-2 text-center">
                                {formatBytes(data.size)}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white/70 backdrop-blur-md mt-16">
        <div className="container mx-auto px-4 py-6 text-center">
          <p className="text-sm text-slate-600">
            All processing happens locally in your browser. Your images are never uploaded to any server.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
